import { HttpError, useOne } from "@refinedev/core";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { IBuildEventPageForm, IOrgIdsItem, IPartnerIdsItem } from "interfaces";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { formatNumber } from "utills/helpers";

export const InternalSetup = ({ isEdit, id }: { isEdit: boolean, id: string | null }) => {
  const {
    watch,
    register,
    control,
    getFieldState,
    formState: { errors },
  } = useFormContext<IBuildEventPageForm>(); // retrieve all hook methods

  const [trigger, setTrigger] = useState(false);

  useEffect(() => {
    if (isEdit && !trigger) {
      setTrigger(true);
    }
  }, [isEdit, trigger]);

  const { data: orgIds, isLoading: isLoadingOrganzization } = useOne<
    IOrgIdsItem[],
    HttpError
  >({
    resource: "organization/ids",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(trigger || id),
    } as any,
  });

  const { data: partnerData, isLoading: isLoadingPartners } = useOne<
    IPartnerIdsItem[],
    HttpError
  >({
    resource: "partner/dropdown",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(trigger || id),
    } as any,
  });

  const organizationIds = (orgIds?.data ?? []).map((org: IOrgIdsItem) => ({
    value: org.id,
    label: org.name_en,
  }));

  const partnerIds = (partnerData?.data ?? []).map((partner) => ({
    value: partner.id,
    label: partner.name,
  }));

  return (
    <div className="box-container p-4 sm:p-6">
      {!isEdit ? (
        (!getFieldState("internal_setup").isTouched ||
        errors["internal_setup"]) && !id ? (
          <p>
            Select the organization or department hosting the internal event
            from the dropdown menu.
          </p>
        ) : (
          <div className="flex flex-col justify-center items-start">
            <div className="w-full flex flex-row items-start gap-x-2">
              <p className="w-40 shrink-0 font-bold">Organization:</p>
              <p>
                {
                  organizationIds.find(
                    (organization) =>
                      organization.value === watch("internal_setup").merchant_id
                  )?.label
                }
              </p>
            </div>
            <div className="w-full flex flex-row items-start gap-x-2 mt-2">
              <p className="w-40 shrink-0 font-bold">Apply for partner:</p>
              <div>
                {partnerIds
                  .filter((partner) =>
                    watch("internal_setup").apply_for_partners.includes(
                      partner.value
                    )
                  )
                  .map((partner, index) => (
                    <p className="text-sm inline" key={partner.value}>
                      {index !== 0 ? <span className="mx-2">•</span> : ""}
                      {partner.label}
                    </p>
                  ))}
              </div>
            </div>
            <div className="w-full flex flex-row items-start gap-x-2 mt-2">
              <p className="w-40 shrink-0 font-bold">MTB id:</p>
              <p>{watch("internal_setup").mtb_id}</p>
            </div>
          </div>
        )
      ) : (
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
          <div className="sm:col-span-3">
            <Label
              id="internal_setup.merchant_id"
              name="Organization Name"
              required
            />
            <Controller
              control={control}
              name="internal_setup.merchant_id"
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  key={`internal_setup.merchant_id`}
                  inputRef={ref}
                  options={organizationIds}
                  id="event_ids"
                  placeholder="Select Org name"
                  defaultValue={
                    organizationIds.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={organizationIds.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={get(errors, `internal_setup.merchant_id`, "")}
                  isLoading={isLoadingOrganzization}
                />
              )}
            />
            {errors["internal_setup"]?.merchant_id ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["internal_setup"]?.merchant_id.message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="internal_setup.apply_for_partners"
              name="Apply for partners"
            />
            <Controller
              control={control}
              name="internal_setup.apply_for_partners"
              defaultValue={[]}
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  isMulti
                  key={``}
                  inputRef={ref}
                  options={partnerIds}
                  id="internal_setup.apply_for_partners"
                  placeholder="Select Partners"
                  value={value.map((id: string) => {
                    return partnerIds.find((item) => item.value === id);
                  })}
                  onChange={(selected: { label: string; value: string }[]) =>
                    onChange(selected.map((item) => `${item.value}`))
                  }
                  error={errors["internal_setup"]?.apply_for_partners}
                  isLoading={isLoadingPartners}
                />
              )}
            />
            {errors["internal_setup"]?.apply_for_partners ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["internal_setup"]?.apply_for_partners.message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label id="internal_setup.mtb_id" name="MTB id" />

            <Input
              id="internal_setup.mtb_id"
              type="text"
              error={errors["internal_setup"]?.mtb_id}
              placeholder="MTB id"
              {...register("internal_setup.mtb_id")}
            />
            {get(errors, `internal_setup.mtb_id`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `internal_setup.mtb_id.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
